import { extend } from 'underscore';
import { User, BaseModel, BaseModelString, KeyValuePair } from './generic.model';
import { SupplierContactType, Role } from './masterdata.model';
import { DiversitySelection, OnboardingDocument, SupplierDiversity } from './onboarding.model';
import { AdditionalTaxInfoValue, WithHoldingResponse } from './tax.model';

export enum Permission {
  NoAccess = 'NoAccess',
  ReadMasked = 'ReadMasked',
  Read = 'Read',
  Write = 'Write',
  ReadWrite = 'ReadWrite',
}

export class PropertyValue<T> {
  oldValue: T;
  newValue: T;
  isTouched = false;
  permission: Permission;
  audits: AuditPropertyValue<T>[];

  constructor() {
    this.permission = Permission.ReadWrite;
  }
}

export enum OPERATION {
  CREATE = 'Create',
  UPDATE = 'Update',
  DELETE = 'Delete'
}

export class Supplier {
  Details: SupplierBasicDetails;
  Diversity: SupplierDiversity;
  Addresses: SupplierAddress[];
  Contacts: SupplierContact[];
  Domains: EmailDomain[];
  Banks: SupplierBank[];
  Taxes: SupplierTax[];
  Documents: SupplierDocument[];
  Entities: SupplierEntity[];
  PurchaseOrderList: SupplierPurchaseOrder[];
}

export class SupplierBasicDetails {
  Operation: OPERATION;
  SupplierID: number;
  SupplierCode: string;
  LegalName: string;
  DBAName: string;
  LocalName: string;
  ShortName: string;
  AccountGroup: string;
  RiskFactor: string;
  SupplierTypes: string[];
  SupplierCategories: string[];
  EDIEnabled: boolean;
  SerializationCode: string;
  InactiveDate: Date;
  ActivatedBy: string;
  MeasurementSystem: string;
  Comments: string;
  Status: string;
  ActiveFlag: boolean;
  IsActive: boolean;
  DiversityDocumentTypes: string[];
  AssessmentDate: Date;
  AssessmentScore: Number;
  AssessedUserName: string;
  OwnerUserId: string;
  OwnerStartDate: string;
  SupportOwnerUserId: number;
  PreferredBuyerName: string;
  CreateDate: Date;
  CreatedBy: User;
  ModifyDate: Date;
  ModifiedBy: User;
}

export class SupplierAddress {
  Operation: OPERATION;
  ID: number;
  StageID: string;
  Types: string[];
  Street1: string;
  Street2: string;
  Street3: string;
  City: string;
  State: string;
  CountryCode: string;
  ZipCode: string;
  Comment: string;
  Documents: string[];
  DocumentInfos: OnboardingDocument[];
  EntityCountryIDList?: number[];
}

export class SupplierMSME {
  Operation: OPERATION;
  ID: number;
  StageID: string;
  Documents: string[];
  MSMEEligible: boolean;
  Response: any;
  RemitToCountry: string;
  DocumentInfos: OnboardingDocument[];
  Acknowledge: boolean;
}

export class NavigationList {
  Name: string;
  Url: string;
  DisplayOrder: number;
  ImageUrl: string;
  ClassName: string;
  OpenInNewTab: boolean;
  NavigationItems: NavigationList[];
}

export class MfaDeclinedReasons {
  DeclinedReasons: string[];
  Comment: string;
}

export class SupplierContact {
  Operation: OPERATION;
  ID: number;
  StageID: string;
  FirstName: string;
  LastName: string;
  JobTitle: string;
  Mobile: string;
  Phone: string;
  SupplierCodes: string[];
  Fax: string;
  Email: string;
  LanguageCode: string;
  Types: string[];
  CreatedBy: User;
  ModifiedBy: User;
  CreatedDate: Date;
  ModifiedDate: Date;
  Comment: string;
  ContactId: number;
  SSOId: string;
  ITchecking: boolean;
  Documents: string[];
  MFADeclinedReasons: string[];
  MFADeclinedComment: string;
  DocumentInfos: OnboardingDocument[];
  SkipResidenceCountry: boolean;
  ResidenceCountry: string;
  HasValidDomain: string;
  AddToValidDomain: string;
  Reason: string;
  Website: string;
  DomainComment: string;
  AdditionalReason: string;
  IsMFAEnrolled: PropertyValue<boolean>;

  isValid(): boolean {
    return this.JobTitle != null && this.JobTitle?.trim() != ""
      && this.Phone != null && this.Phone?.trim() != ""
      && (this.SkipResidenceCountry || (this.ResidenceCountry != null && this.ResidenceCountry?.trim() != ""));
  }

  static isValid(contact: SupplierContact): boolean {
    if (!contact) return false;
    return contact.JobTitle != null && contact.JobTitle?.trim() != ""
      && contact.Phone != null && contact.Phone?.trim() != ""
      && (contact.SkipResidenceCountry || (contact.ResidenceCountry != null && contact.ResidenceCountry?.trim() != ""));
  }
}

export class SupplierBank {
  Operation: OPERATION;
  SupplierCode: string;
  ID: number;
  StageID: string;
  EntityID: string;
  BankName: string;
  BankLocalName: string;
  DisplayName: string;
  BankNickName: string;
  BeneficiaryNameEnglish: string;
  BeneficiaryNameDifferentReason?: string;
  BeneficiaryNameDifferentReasonComment?: string;
  BeneficiaryNameLocal: string;
  CurrencyCode: string;
  CountryCode: string;
  PaymentOrigin: string;
  MethodOfPaymentID: number;
  PaymentType: string;
  BankAccountType: string;
  BankAccountNameKatanaka: string;
  Address1: string;
  Address2: string;
  City: string;
  State: string;
  ZipCode: number;
  ABAWire: number;
  IBANCode: string;
  IBANCodeExists: boolean;
  IntermediaryBankAccountExists: boolean;
  IntermediaryPaymentOrigin: string;
  SwiftCode: string;
  SortCode: string;
  VATNumber: string;
  ABANumber: number;
  ABARoutingNumber: number;
  AccountNumber: string;
  AccountNumberExists: boolean;
  Comments: string;
  Documents: string[];
  DocumentInfos: OnboardingDocument[];
  ModifyDate: Date;
  ModifiedBy: string;
  CreateDate: Date;
  CreatedBy: User;
  Last4AccountNumber: string;
  Last4IBANCode: string;
  Last4IntermediaryBankAccount: string;
  IsNewBankAccount: boolean;
  IsNewIntermediaryBankAccount: boolean;
  IsIntermediaryBanking: boolean;
  IntermediaryBankName: string;
  IntermediaryBankRouting: string;
  IntermediaryBankSwiftCode: string;
  IntermediaryBankAccount: string;
  IntermediaryAddress1: string;
  IntermediaryAddress2: string;
  IntermediaryCity: string;
  IntermediaryState: string;
  IntermediaryZipCode: number;
  IntermediaryCountryCode: string;
  VerificationStatus: string;
  VerificationTransactionId: string;
  IsPrimary?: boolean;
  IsValid?: boolean;
  SkipDocumentValidation?: boolean;
  EntityCountryIDList?: number[];
  TempID?: number;
  IsModified?: boolean;
  IsSecurityCheckRequired?: boolean;
}

export class SupplierTax {
  Operation: OPERATION;
  ID: number;
  StageID: string;
  FederalTaxID: string;
  TaxIDTypes: string;
  IsRelative: boolean;
  TaxName: string;
  Corporate: boolean;
  Tax1099Box: string;
  W9Received: boolean;
  W8Received: boolean;
  Documents: string[];
  ForiegnEntity: boolean;
  VATNumber: string;
  ModifyDate: Date;
  ModifiedBy: User;
  CreateDate: Date;
  CreatedBy: User;
  Last4FederalTaxID: string;
  Report1099: boolean;
  DoYouHaveVat: string;
  IsNewRecord: boolean;
  FederalTaxIDExists: boolean;
  Classification: string;
  ClassificationType: string;
  OtherCompany: string;
  TaxCountry: string;
  DocumentInfos: OnboardingDocument[];
  WithHoldingSelection: string[];
  FileFederalTax?: boolean;
  EntityType: string;
  IsPrimary?: boolean;
  IsValid?: boolean;
  RequiredDocuments?: OnboardingDocument[];
  EntityCountryIDList?: number[];
  CompanyType: string;
  WithHoldingResponse: WithHoldingResponse[];
  AdditionalTaxIds: AdditionalTaxInfoValue[];
}

export class SupplierDocument {
  Operation: OPERATION;
  ID: number;
  StageID: string;
  DocumentID: string;
  DocumentVersion: string;
  ReferenceSystem: string;
  ESignAgreementId: string;
  FileName: string;
  DocumentPath: string;
  DocumentType: string;
  IsValid: boolean;
  IsContractNotRequired: boolean;
  NoEffectiveDate: boolean;
  NoExpirationDate: boolean;
  NoSignedDate: boolean;
  Attachment: string | File;
  AttachmentPath: string;
  SignedDate: Date;
  EffectiveDate: Date;
  ExpiryDate: Date;
  ModifiedDate: Date;
  AllowedExtensions: string;
  File: File;
  FileAsString: string;
}

export class SupplierEntity {
  Operation: OPERATION;
  ID: number;
  StageID: string;
  EntityName: string;
  AddressID: number | string;
  ContactID: number | string;
  BankID: number | string;
  TaxInfoID: number | string;
  MethodOfPaymentID: number;
  IncoTermCode: string;
  CurrencyCode: string;
  NetTermCode: string;
  IsActive: boolean;
  ModifiedDate: Date;
  ModifiedBy: User;
}
export class SupplierPurchaseOrder {
  PurchaseOrderID: number;
  SAPPONumber: string;
  POType: string;
  PartNumber: string;
  UnitOfMeasure: string;
  OpenQuantity: number;
  BuyerName: string;
  PlannerName: string;
  DesiredReceiveDate: Date;
  CreateDate: Date;
  OrderDate: Date;
}

export class PortalAccountApprover {
  ID: number;
  Approver: User;
  Group: string;
}


export class SupplierCategory extends BaseModel {
  TypeId: number;
  TaxReportable: boolean;
  NetTermCode: string;
  IncoTermCode: string;
}
export class SupplierContactTypeGroup {
  Name: string;
  Types: Role[];
}

export class Language extends BaseModelString {
  country: string;
  localizedName: string;
  localizedCountryName: string;
  regionID: number;
}

export class TranslateLanguage extends BaseModelString {
  name: string;
  id: string;
  createdBy?: string;
  createdTimeStamp?: string;
  modifiedBy?: string;
  modifiedTimeStamp?: string;
  isDeleted?: string;
  deletedBy?: string;
  deletedTimeStamp?: string;
}

export class Region extends BaseModelString {
  regionID: number;
}
export class SupplierType extends BaseModel {
  Categories: SupplierCategory[];
}
export class MethodOfPayment extends BaseModel {
  Mode: string;
  EntityID: number;
}
export class Entity {
  EntityID: number;
  Name: string;
  Description: string;
  Region: string;
}
export class Currency extends BaseModel {
  CODE: string;
}
export class SupplierConflictOfInterest extends BaseModel { }
export class SupplierAnnualSpend extends BaseModel { }
export class SupplierAccountGroup extends BaseModel { }
export class SupplierRiskFactor extends BaseModel { }
export class SupplierPackagingSystem extends BaseModel {
}

export class SupplierTaxIDType extends BaseModel {
}

export class SupplierTax1099Box extends BaseModel {
}

export class SupplierDocumentType extends BaseModel {
  Category: DocumentTypeCategory;
}

export class MultiDocument {
  DocumentType: string;
  Attachments: SupplierDocument[];
  IsRequired: boolean;
  EffectiveDate?: Date | any;
  ExpiryDate?: Date | any;
}

export class SupplierDocumentTypeByCountry extends BaseModel {
  Category: DocumentTypeCategory;
  Required: boolean;
  ESignable: boolean;
  DefaultOption: string;
}

export class BankAccount {
  SupplierBankAccountID: number;
  BankName: string;
  BankLocalName: string;
  CountryID: number;
  IsNewBankAccount: boolean;
  ABANumber: string;
  ABAWire: string;
  AccountNumber: string;
  AccountNumberExists: boolean;
  VATNumber: string;
  IBANCode: string;
  IBANCodeExists: boolean;
  SwiftCode: string;
  ABARoutingNumber: string;
  BankNickName: string;
  BankCurrencyID: string;
  MethodOfPaymentID: number;
  SortCode: string;
  BankGiro: string;
  PaymentMode: string;
}


export class Error {
  errorCode: string;
  errorMsg: string;
}

export class BankConfiguration {
  accountNumber: BankConfig;
  iban: BankConfig;
  bankLocalName: BankConfig;
  paymentType: BankConfig;
  routingCode: BankConfig;
  swiftCode: BankConfig;
  bankAccountKatanaka: BankConfig;
  bankAccountType: BankConfig;
  beneficiaryNameEnglish: BankConfig;
  beneficiaryNameLocal: BankConfig;
  address1: BankConfig;
  address2: BankConfig;
  city: BankConfig;
  state: BankConfig;
  zipCode: BankConfig;
}

export class BankConfig {
  label: string;
  helpText: string;
  type: string;
  required: string;
  minSize: number;
  maxSize: number;
  values: any;
}

export class BankValidationRequest {
  isNewBankAccount: boolean;
  beneficiaryNameEnglish: string;
  beneficiaryNameLocalLanguage: string;
  bankAccountKatanaka: string;
  bankAccountTypeSet: string;
  paymentType: string;
  paymentOrigin: string;
  bankCountryId: string;
  bankIBAN: string;
  bankLocalName: string;
  bankAccountId: string;
  bankRoutingCode: string;
  bankSwift: string;
  streetAddressLine1: string;
  streetAddressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  id: number;
  isNewIntermediaryBankAccount: boolean;
  isIntermediaryBanking: boolean;
  intermediaryBankName: string;
  intermediaryPaymentOrigin: string;
  intermediaryBankRouting: string;
  intermediaryBankSwiftCode: string;
  intermediaryBankAccount: string;
  intermediaryAddress1: string;
  intermediaryAddress2: string;
  intermediaryCity: string;
  intermediaryState: string;
  intermediaryZipCode: number;
  intermediaryCountryCode: string;
  verificationStatus: string;
  verificationTransactionId: string;
}

export class DataWithSupplierCode<T> {
  suppliercode: string;
  currentUser: any;
  data: T;
  isManageContact: boolean;
  isOnboarding?: boolean;
}


export class SupplierOnboarding {
  type: string;
  supplierCode: string;
  name: string;
  supplierType: number;
  supplierCategory: number;
  documentTypeIds: string;
  nDASignType: string;
  status: string;
  statusDescription: string;
  contacts: OnboardingContact[]
}

export class OnboardingContact {
  firstName: string;
  lastName: string;
  email: string;
  isPrimary: boolean;
}

export class GridViewModel<T> {
  data: T[];
  total: number;

  constructor() {
    this.data = [];
    this.total = 0;
  }
}

export class RejectionData {
  RejectToSupplier: boolean;
  ReasonCode: string;
  Comment: string;
}

export enum OnBoardingStatus {
  PendingSupplier = 'SupplierActionPending',
  PendingRequestor = 'RequestorActionPending',
  GSMApprovalPending = 'GSMApprovalPending',
  GSMManagerApprovalPending = 'GSMManagerApprovalPending',
  VVTApprovalPending = 'VVTApprovalPending',
  Rejected = 'Rejected',
  Approved = 'Approved',
  Cancelled = 'Cancelled'
}

export class ActionFlags {
  canLinkContacts = false;
  canApprove = false;
  canReject = false;
  canSubmit = false;
  isVVT = false;
  isGSM = false;
  isProcOps = false;
  isRequestPendingForApproval = false;
  isPendingWithSupplier = false;
  isDashboardTask = false;
  isRFQPending = false;
}

export enum DocumentTypeCategory {
  Bank = 'Bank',
  Address = 'Address'
}

export class DocumentType {
  constructor(public documentTypeID: number,
    public name: string,
    public description: string,
    public allowedExtensions: string,
    public category: DocumentTypeCategory,
    public isDocumentAvailableForSupplier: boolean,
    public associatedCategoryLinkId: string[]) { }
}

export class Audit {
  Event: string;
  Data: JSON;
  RequestedBy: User;
  RequestedDate: Date;
  Action: string;
  ActionBy: User;
  ActionDate: Date;

}

export class SupplierPropertyValue {
  details: SupplierBasicDetailsPropertyValue;
  diversity: SupplierDiversityPropertyValue;
  addresses: SupplierAddressPropertyValues[];
  contacts: SupplierContactPropertyValue[];
  domains: EmailDomainPropertyValue[];
  banks: SupplierBankPropertyValue[];
  taxes: SupplierTaxPropertyValues[];
  documents: SupplierDocumentPropertyValues[];
  entities: SupplierEntityPropertyValues[];
  purchaseOrderList: SupplierPurchaseOrder[];
  ehsResponse: SupplierEhsResponsePropertyValue[];
  channels: SupplierChannelPropertyValues[]
}

export class SupplierProfileEvent {
  id: number;
  properties: BankSecurityAnswer;
  correlationId: string;
  status: string;
  reason: string;
}

export class BankSecurityEvent {
  maxAttempts: number;
  wrongAttempts: number;
  lastEvent: SupplierProfileEvent;
  token: string;
}

export class BankSecurityAnswer {
  supplierCode: string;
  isLastPaymentReceived: boolean;
  lastPaymentReceived: number;
  currency: string;
  changeReason: string;
  accountNumber: string;
  beneficiary: string;
  aROutSourced: boolean;
}

export class BankSecurityValidationResponse {
  hasErrors: boolean;
  profileEvent: SupplierProfileEvent;
  errors: Array<string>;
  token: string;
}

export class SupplierBasicDetailsPropertyValue {
  operation: PropertyValue<OPERATION>;
  isTouched = false;
  legalName: PropertyValue<string>;
  dBAName: PropertyValue<string>;
  localName: PropertyValue<string>;
  website: PropertyValue<string>;
  shortName: PropertyValue<string>;
  accountGroup: PropertyValue<string>;
  riskFactor: PropertyValue<string>;
  supplierTypes: PropertyValue<string[]>;
  supplierCategories: PropertyValue<string[]>;
  tierLevel: PropertyValue<string>;
  eDIEnabled: PropertyValue<boolean>;
  serializationCode: PropertyValue<string>;
  inactiveDate: PropertyValue<Date>;
  deActivatedByUserId: PropertyValue<number>;
  measurementSystem: PropertyValue<string>;
  comments: PropertyValue<string>;
  diversityDocumentTypes: PropertyValue<string[]>;
  status: string;

  assessmentDate: PropertyValue<Date>;
  assessmentScore: PropertyValue<Number>;
  assessedUserName: PropertyValue<string>;
  ownerUserId: PropertyValue<number>;
  ownerStartDate: PropertyValue<string>;
  supportOwnerUserId: PropertyValue<number>;
  preferredBuyerName: PropertyValue<string>;

  activeFlag: PropertyValue<boolean>;
  isActive: PropertyValue<boolean>;

  createDate: Date;
  createdBy: User;
  modifyDate: Date;
  modifiedBy: User;

  constructor() {
    this.legalName = new PropertyValue<string>();
    this.dBAName = new PropertyValue<string>();
    this.localName = new PropertyValue<string>();
    this.shortName = new PropertyValue<string>();
    this.website = new PropertyValue<string>();
    this.accountGroup = new PropertyValue<string>();
    this.riskFactor = new PropertyValue<string>();
    this.supplierTypes = new PropertyValue<string[]>();
    this.supplierCategories = new PropertyValue<string[]>();
    this.eDIEnabled = new PropertyValue<boolean>();
    this.serializationCode = new PropertyValue<string>();
    this.inactiveDate = new PropertyValue<Date>();
    this.deActivatedByUserId = new PropertyValue<number>();
    this.measurementSystem = new PropertyValue<string>();
    this.comments = new PropertyValue<string>();
    this.activeFlag = new PropertyValue<boolean>();
    this.isActive = new PropertyValue<boolean>();
    this.operation = new PropertyValue<OPERATION>();

    this.assessmentDate = new PropertyValue<Date>();
    this.assessmentScore = new PropertyValue<Number>();
    this.assessedUserName = new PropertyValue<string>();
    this.ownerUserId = new PropertyValue<number>();
    this.ownerStartDate = new PropertyValue<string>();
    this.supportOwnerUserId = new PropertyValue<number>();
    this.preferredBuyerName = new PropertyValue<string>();

  }
}

export class SupplierDiversityPropertyValue {
  constructor() {
    this.operation = new PropertyValue<OPERATION>();
    this.isDiversityOwned = new PropertyValue<string>();
    this.diversityTypes = new PropertyValue<DiversitySelection[]>();
    this.ownershipPercentage = new PropertyValue<number>();
    this.isCouncilCertified = new PropertyValue<string>();
    this.councilCertifications = new PropertyValue<DiversitySelection[]>();
    this.isGovtCertified = new PropertyValue<string>();
    this.govtCertifications = new PropertyValue<DiversitySelection[]>();
    this.documentIds = new PropertyValue<string[]>();
    this.documents = new PropertyValue<SupplierDocument[]>();
  }

  operation: PropertyValue<OPERATION>;
  isDiversityOwned: PropertyValue<string>;
  diversityTypes: PropertyValue<DiversitySelection[]>;
  ownershipPercentage: PropertyValue<number>;
  isCouncilCertified: PropertyValue<string>;
  councilCertifications: PropertyValue<DiversitySelection[]>;
  isGovtCertified: PropertyValue<string>;
  govtCertifications: PropertyValue<DiversitySelection[]>;
  documentIds: PropertyValue<string[]>;
  documents: PropertyValue<SupplierDocument[]>;
}

export class SupplierAddressPropertyValues {
  operation: PropertyValue<OPERATION>;
  isTouched = false;
  id: PropertyValue<number>;
  stageID: PropertyValue<string>;
  types: PropertyValue<string[]>;
  street1: PropertyValue<string>;
  street2: PropertyValue<string>;
  street3: PropertyValue<string>;
  city: PropertyValue<string>;
  state: PropertyValue<string>;
  countryCode: PropertyValue<string>;
  zipCode: PropertyValue<string>;
  comment: PropertyValue<string>;
  documents: PropertyValue<string[]>;
  attachments: File;
  addressType: string;
  locationName: string;
  warehouseAddressId: number;
  manufacturingAddressId: number;
  contacts: PropertyValue<string[]>;
  locationId: PropertyValue<number>;

  constructor() {
    this.types = new PropertyValue<string[]>();
    this.street1 = new PropertyValue<string>();
    this.street2 = new PropertyValue<string>();
    this.street3 = new PropertyValue<string>();
    this.city = new PropertyValue<string>();
    this.state = new PropertyValue<string>();
    this.countryCode = new PropertyValue<string>();
    this.zipCode = new PropertyValue<string>();
    this.comment = new PropertyValue<string>();
    this.operation = new PropertyValue<OPERATION>();
    this.documents = new PropertyValue<string[]>();
    this.contacts = new PropertyValue<string[]>();
    this.locationId = new PropertyValue<number>();
  }
}

export class SupplierContactPropertyValue {
  operation: PropertyValue<OPERATION>;
  isTouched = false;
  isValid = false;
  id: PropertyValue<number>;
  stageID: PropertyValue<string>;
  firstName: PropertyValue<string>;
  lastName: PropertyValue<string>;
  jobTitle: PropertyValue<string>;
  mobile: PropertyValue<string>;
  phone: PropertyValue<string>;
  supplierCodes: PropertyValue<string[]>;
  fax: PropertyValue<string>;
  iTchecking: PropertyValue<boolean>;
  email: PropertyValue<string>;
  languageCode: PropertyValue<string>;
  residenceCountry: PropertyValue<string>;
  skipResidenceCountry: PropertyValue<boolean>;
  types: PropertyValue<string[]>;
  createdBy: User;
  modifiedBy: User;
  createdDate: Date;
  modifiedDate: Date;
  comment: PropertyValue<string>;
  contactId: PropertyValue<number>;
  ssoId: PropertyValue<string>;
  canUpdateItem: Boolean;
  canDeleteItem: Boolean;
  documents: PropertyValue<string[]>;
  hasValidDomain: PropertyValue<string>;
  addToValidDomain: PropertyValue<string>;
  reason: PropertyValue<string>;
  website: PropertyValue<string>;
  additionalReason: PropertyValue<string>;
  domainComment: PropertyValue<string>;
  domain: PropertyValue<string>;
  isMFAEnrolled: PropertyValue<boolean>;
  hasApprovedPcfOnFile: PropertyValue<boolean>;

  constructor() {
    this.firstName = new PropertyValue<string>();
    this.lastName = new PropertyValue<string>();
    this.jobTitle = new PropertyValue<string>();
    this.mobile = new PropertyValue<string>();
    this.phone = new PropertyValue<string>();
    this.supplierCodes = new PropertyValue<string[]>();
    this.fax = new PropertyValue<string>();
    this.email = new PropertyValue<string>();
    this.languageCode = new PropertyValue<string>();
    this.types = new PropertyValue<string[]>();
    this.comment = new PropertyValue<string>();
    this.operation = new PropertyValue<OPERATION>();
    this.contactId = new PropertyValue<number>();
    this.ssoId = new PropertyValue<string>();
    this.documents = new PropertyValue<string[]>();
    this.iTchecking = new PropertyValue<boolean>();
    this.residenceCountry = new PropertyValue<string>();
    this.skipResidenceCountry = new PropertyValue<boolean>();
    this.hasValidDomain = new PropertyValue<string>();
    this.addToValidDomain = new PropertyValue<string>();
    this.reason = new PropertyValue<string>();
    this.website = new PropertyValue<string>();
    this.domainComment = new PropertyValue<string>();
    this.domain = new PropertyValue<string>();
    this.additionalReason = new PropertyValue<string>();
    this.isMFAEnrolled = new PropertyValue<boolean>();
    this.hasApprovedPcfOnFile = new PropertyValue<boolean>();
  }
}

export class EmailDomain {
  operation: OPERATION;
  id: number;
  additionalReason: string;
  stageID: string;
  website: string;
  domain: string;
  reason: string;
  comment: string;
  documents: any[];
  documentInfos?: SupplierDocument[];
}

export class EmailDomainPropertyValue {
  constructor() {
    this.operation = new PropertyValue<OPERATION>();
    this.website = new PropertyValue<string>();
    this.domain = new PropertyValue<string>();
    this.reason = new PropertyValue<string>();
    this.comment = new PropertyValue<string>();
    this.additionalReason = new PropertyValue<string>();
    this.documents = new PropertyValue<string[]>();
    this.id = new PropertyValue<number>();
    this.disableDomainEdit = new PropertyValue<boolean>();
  }
  canUpdateItem: boolean;
  canDeleteItem: boolean;
  operation: PropertyValue<OPERATION>;
  isTouched = false;
  id: PropertyValue<number>;
  stageID: PropertyValue<string>;
  additionalReason: PropertyValue<string>;
  website: PropertyValue<string>;
  reason: PropertyValue<string>;
  comment: PropertyValue<string>;
  domain: PropertyValue<string>;
  disableDomainEdit: PropertyValue<boolean>;
  documents: PropertyValue<string[]>;
}

export class SupplierBankPropertyValue {
  operation: PropertyValue<OPERATION>;
  isTouched = false;
  supplierCode: string;
  paymentOrigin: PropertyValue<string>;
  id: PropertyValue<number>;
  stageID: PropertyValue<string>;
  bankName: PropertyValue<string>;
  bankLocalName: PropertyValue<string>;
  beneficiaryNameEnglish: PropertyValue<string>;
  beneficiaryNameDifferentReason: PropertyValue<string>;
  beneficiaryNameDifferentReasonComment: PropertyValue<string>;
  beneficiaryNameLocal: PropertyValue<string>;
  displayName: PropertyValue<string>;
  bankNickName: PropertyValue<string>;
  currencyCode: PropertyValue<string>;
  countryCode: PropertyValue<string>;
  methodOfPaymentID: PropertyValue<number>;
  paymentType: PropertyValue<string>;
  bankAccountType: PropertyValue<string>;
  bankAccountNameKatanaka: PropertyValue<string>;
  address1: PropertyValue<string>;
  address2: PropertyValue<string>;
  city: PropertyValue<string>;
  state: PropertyValue<string>;
  zipCode: PropertyValue<number>;
  aBAWire: PropertyValue<number>;
  ibanCode: PropertyValue<string>;
  last4IBANCode: PropertyValue<string>;
  swiftCode: PropertyValue<string>;
  sortCode: PropertyValue<string>;
  vATNumber: PropertyValue<string>;
  taxCountry: PropertyValue<string>;
  abaNumber: PropertyValue<number>;
  aBARoutingNumber: PropertyValue<number>;
  accountNumber: PropertyValue<string>;
  last4AccountNumber: PropertyValue<string>;
  last4IntermediaryBankAccount: PropertyValue<string>;
  paymentMethodID: PropertyValue<number>;
  comments: PropertyValue<string>;
  documents: PropertyValue<string[]>;
  modifyDate: PropertyValue<Date>;
  modifiedBy: PropertyValue<string>;
  createDate: PropertyValue<Date>;
  createdBy: PropertyValue<User>;
  isNewBankAccount: PropertyValue<boolean>;
  isNewIntermediaryBankAccount: PropertyValue<boolean>;
  isIntermediaryBanking: PropertyValue<boolean>;
  intermediaryPaymentOrigin: PropertyValue<string>;
  intermediaryBankName: PropertyValue<string>;
  intermediaryBankRouting: PropertyValue<string>;
  intermediaryBankSwiftCode: PropertyValue<string>;
  intermediaryBankAccount: PropertyValue<string>;
  intermediaryAddress1: PropertyValue<string>;
  intermediaryAddress2: PropertyValue<string>;
  intermediaryCity: PropertyValue<string>;
  intermediaryState: PropertyValue<string>;
  intermediaryZipCode: PropertyValue<number>;
  intermediaryCountryCode: PropertyValue<string>;
  verificationStatus: PropertyValue<string>;
  verificationTransactionId: PropertyValue<string>;

  constructor() {
    this.operation = new PropertyValue<OPERATION>();
    this.isTouched = false;
    this.supplierCode = null;
    this.paymentOrigin = new PropertyValue<string>();
    this.id = new PropertyValue<number>();
    this.stageID = new PropertyValue<string>();
    this.bankName = new PropertyValue<string>();
    this.bankLocalName = new PropertyValue<string>();
    this.beneficiaryNameEnglish = new PropertyValue<string>();
    this.beneficiaryNameDifferentReason = new PropertyValue<string>();
    this.beneficiaryNameDifferentReasonComment = new PropertyValue<string>();
    this.beneficiaryNameLocal = new PropertyValue<string>();
    this.displayName = new PropertyValue<string>();
    this.bankNickName = new PropertyValue<string>();
    this.currencyCode = new PropertyValue<string>();
    this.countryCode = new PropertyValue<string>();
    this.methodOfPaymentID = new PropertyValue<number>();
    this.paymentType = new PropertyValue<string>();
    this.bankAccountType = new PropertyValue<string>();
    this.bankAccountNameKatanaka = new PropertyValue<string>();
    this.address1 = new PropertyValue<string>();
    this.address2 = new PropertyValue<string>();
    this.city = new PropertyValue<string>();
    this.state = new PropertyValue<string>();
    this.zipCode = new PropertyValue<number>();
    this.aBAWire = new PropertyValue<number>();
    this.ibanCode = new PropertyValue<string>();
    this.last4IBANCode = new PropertyValue<string>();
    this.swiftCode = new PropertyValue<string>();
    this.sortCode = new PropertyValue<string>();
    this.vATNumber = new PropertyValue<string>();
    this.taxCountry = new PropertyValue<string>();
    this.abaNumber = new PropertyValue<number>();
    this.aBARoutingNumber = new PropertyValue<number>();
    this.accountNumber = new PropertyValue<string>();
    this.last4AccountNumber = new PropertyValue<string>();
    this.last4IntermediaryBankAccount = new PropertyValue<string>();
    this.paymentMethodID = new PropertyValue<number>();
    this.comments = new PropertyValue<string>();
    this.documents = new PropertyValue<string[]>();
    this.modifyDate = new PropertyValue<Date>();
    this.modifiedBy = new PropertyValue<string>();
    this.createDate = new PropertyValue<Date>();
    this.createdBy = new PropertyValue<User>();
    this.isNewBankAccount = new PropertyValue<boolean>();
    this.isNewIntermediaryBankAccount = new PropertyValue<boolean>();
    this.isIntermediaryBanking = new PropertyValue<boolean>();
    this.intermediaryPaymentOrigin = new PropertyValue<string>();
    this.intermediaryBankName = new PropertyValue<string>();
    this.intermediaryBankRouting = new PropertyValue<string>();
    this.intermediaryBankSwiftCode = new PropertyValue<string>();
    this.intermediaryBankAccount = new PropertyValue<string>();
    this.intermediaryAddress1 = new PropertyValue<string>();
    this.intermediaryAddress2 = new PropertyValue<string>();
    this.intermediaryCity = new PropertyValue<string>();
    this.intermediaryState = new PropertyValue<string>();
    this.intermediaryZipCode = new PropertyValue<number>();
    this.intermediaryCountryCode = new PropertyValue<string>();
    this.verificationStatus = new PropertyValue<string>();
    this.verificationTransactionId = new PropertyValue<string>();
  }
}

export class SupplierTaxPropertyValues {
  operation: PropertyValue<OPERATION>;
  isTouched = false;
  id: PropertyValue<number>;
  stageID: PropertyValue<string>;
  supplierID: PropertyValue<number>;
  federalTaxID: PropertyValue<string>;
  vatNumber: PropertyValue<string>;
  taxIDTypes: PropertyValue<string>;
  corporate: PropertyValue<boolean>;
  taxName: PropertyValue<string>;
  tax1099Box: PropertyValue<string>;
  w9Received: PropertyValue<boolean>;
  isRelative: PropertyValue<boolean>;
  w8Received: PropertyValue<boolean>;
  documents: PropertyValue<string[]>;
  foriegnEntity: PropertyValue<boolean>;
  modifyDate: PropertyValue<Date>;
  modifiedBy: PropertyValue<User>;
  createDate: PropertyValue<Date>;
  createdBy: PropertyValue<User>;
  taxCountry: PropertyValue<string>;
  withHoldingSelection: PropertyValue<string[]>;
  classification: PropertyValue<string>;
  classificationType: PropertyValue<string>;
  otherCompany: PropertyValue<string>;
  isNewRecord: PropertyValue<boolean>;
  fileFederalTax?: PropertyValue<boolean>;
  entityType: PropertyValue<string>;
  companyType: PropertyValue<string>;
  withHoldingResponse: PropertyValue<WithHoldingResponse[]>;
  additionalTaxIds: PropertyValue<AdditionalTaxInfoValue[]>;

  constructor() {
    this.federalTaxID = new PropertyValue<string>();
    this.vatNumber = new PropertyValue<string>();
    this.taxIDTypes = new PropertyValue<string>();
    this.corporate = new PropertyValue<boolean>();
    this.taxName = new PropertyValue<string>();
    this.tax1099Box = new PropertyValue<string>();
    this.w9Received = new PropertyValue<boolean>();
    this.w8Received = new PropertyValue<boolean>();
    this.documents = new PropertyValue<string[]>();
    this.foriegnEntity = new PropertyValue<boolean>();
    this.modifyDate = new PropertyValue<Date>();
    this.modifiedBy = new PropertyValue<User>();
    this.createDate = new PropertyValue<Date>();
    this.createdBy = new PropertyValue<User>();
    this.operation = new PropertyValue<OPERATION>();
    this.isRelative = new PropertyValue<boolean>();
    this.taxCountry = new PropertyValue<string>();
    this.supplierID = new PropertyValue<number>();
    this.id = new PropertyValue<number>();
    this.stageID = new PropertyValue<string>();
    this.withHoldingSelection = new PropertyValue<string[]>();
    this.classification = new PropertyValue<string>();
    this.classificationType = new PropertyValue<string>();
    this.otherCompany = new PropertyValue<string>();
    this.isNewRecord = new PropertyValue<boolean>();
    this.entityType = new PropertyValue<string>();
    this.fileFederalTax = new PropertyValue<boolean>();
    this.companyType = new PropertyValue<string>();
    this.withHoldingResponse = new PropertyValue<WithHoldingResponse[]>();
    this.additionalTaxIds =  new PropertyValue<AdditionalTaxInfoValue[]>();
  }
}


export class SupplierDocumentPropertyValues {
  operation: PropertyValue<OPERATION>;
  isTouched = false;
  documentID: PropertyValue<string>;
  documentVersion: PropertyValue<string>;
  referenceSystem: PropertyValue<string>;
  id: PropertyValue<number>;
  stageID: PropertyValue<string>;
  fileName: PropertyValue<string>;
  documentPath: PropertyValue<string>;
  documentTypeID: PropertyValue<number>;
  eSignAgreementId: PropertyValue<string>;
  documentType: PropertyValue<string>;
  isValid: PropertyValue<boolean>;
  isContractNotRequired: PropertyValue<boolean>;
  noEffectiveDate: PropertyValue<boolean>;
  noExpirationDate: PropertyValue<boolean>;
  noSignedDate: PropertyValue<boolean>;
  attachment: PropertyValue<string | File>;
  attachmentPath: PropertyValue<string>;
  signedDate: PropertyValue<Date>;
  effectiveDate: PropertyValue<Date>;
  expiryDate: PropertyValue<Date>;
  modifiedDate: PropertyValue<Date>;

  constructor() {
    this.operation = new PropertyValue<OPERATION>();
    this.isTouched = false;
    this.documentID = new PropertyValue<string>();
    this.documentVersion = new PropertyValue<string>();
    this.referenceSystem = new PropertyValue<string>();
    this.id = new PropertyValue<number>();
    this.stageID = new PropertyValue<string>();
    this.fileName = new PropertyValue<string>();
    this.documentPath = new PropertyValue<string>();
    this.documentTypeID = new PropertyValue<number>();
    this.eSignAgreementId = new PropertyValue<string>();
    this.documentType = new PropertyValue<string>();
    this.isValid = new PropertyValue<boolean>();
    this.isContractNotRequired = new PropertyValue<boolean>();
    this.noEffectiveDate = new PropertyValue<boolean>();
    this.noExpirationDate = new PropertyValue<boolean>();
    this.noSignedDate = new PropertyValue<boolean>();
    this.attachment = new PropertyValue<string | File>();
    this.attachmentPath = new PropertyValue<string>();
    this.signedDate = new PropertyValue<Date>();
    this.effectiveDate = new PropertyValue<Date>();
    this.expiryDate = new PropertyValue<Date>();
    this.modifiedDate = new PropertyValue<Date>();
  }
}


export class SupplierEntityPropertyValues {
  operation: PropertyValue<OPERATION>;
  isTouched = false;
  id: PropertyValue<number>;
  stageID: PropertyValue<string>;
  entityName: PropertyValue<string>;
  addressID: PropertyValue<number | string>;
  contactID: PropertyValue<number | string>;
  bankID: PropertyValue<number | string>;
  taxInfoID: PropertyValue<number | string>;
  methodOfPaymentID: PropertyValue<number>;
  incoTermCode: PropertyValue<string>;
  currencyCode: PropertyValue<string>;
  netTermCode: PropertyValue<string>;
  isActive: PropertyValue<boolean>;
  modifiedDate: PropertyValue<Date>;
  modifiedBy: PropertyValue<User>;
  aXPublishEvent: PropertyValue<BPMEvent>;

  constructor() {
    this.entityName = new PropertyValue<string>();
    this.addressID = new PropertyValue<number>();
    this.contactID = new PropertyValue<number>();
    this.bankID = new PropertyValue<number>();
    this.taxInfoID = new PropertyValue<number>();
    this.methodOfPaymentID = new PropertyValue<number>();
    this.incoTermCode = new PropertyValue<string>();
    this.currencyCode = new PropertyValue<string>();
    this.netTermCode = new PropertyValue<string>();
    this.isActive = new PropertyValue<boolean>();
    this.modifiedDate = new PropertyValue<Date>();
    this.modifiedBy = new PropertyValue<User>();
    this.operation = new PropertyValue<OPERATION>();
    this.aXPublishEvent = new PropertyValue<BPMEvent>();
  }
}

export class SupplierEhsResponsePropertyValue {
  constructor() {
    this.operation = new PropertyValue<OPERATION>();
    this.templateId = new PropertyValue<number>();
    this.version = new PropertyValue<number>();
    this.response = new PropertyValue<string>();
  }
  operation: PropertyValue<OPERATION>;
  isTouched = false;
  id: PropertyValue<number>;
  stageID: PropertyValue<string>;
  templateId: PropertyValue<number>;
  version: PropertyValue<number>;
  response: PropertyValue<string>;
}

export class SupplierChannelPropertyValues {
  constructor() {
    this.entityName = new PropertyValue<string>();
    this.country = new PropertyValue<string>();
    this.supplierTypes = new PropertyValue<string[]>();
    this.sources = new PropertyValue<AutoInvoiceSupplierSourceMap[]>();
  }
  country: PropertyValue<string>;
  countryid: PropertyValue<number>;
  entityName: PropertyValue<string>;
  id: PropertyValue<number>;
  operation: PropertyValue<string>;
  sources: PropertyValue<AutoInvoiceSupplierSourceMap[]>;
  supplierId: PropertyValue<number>;
  region: PropertyValue<string>;
  supplierTypes: PropertyValue<string[]>;
}

export class SupplierChannels {
  constructor() {
    this.sources = [];
    this.supplierTypes = [];
  }

  country: string;
  countryid: number;
  entityname: string;
  id: number;
  operation: string;
  sources: AutoInvoiceSupplierSourceMap[];
  region: string;
  supplierid: number;
  supplierTypes: string[]
}


export class SupplierChannelGridValues {

  entityName: string;
  countryCode: string;
  supplierCategory: string;
  region: string;
  sources: { SourceName: string, Enabled: boolean, Supplierid: number }[];
}


export class AutoInvoiceSupplierSourceMap {

  AutoInvoiceSupplierSourceMapID: number;
  AutoInvoiceSupplierSourceID: number;
  Enabled: boolean;
  CreatedDate: Date;
  CreatedBy: string;
  ModifiedDate: Date;
  ModifiedBy: string;
  SupplierId: number;
  SupplierCode: string;
  SupplierSourceName: string;
  AllowChannelSelection: boolean;
}

export class BPMEvent {
  Id: string;
  Execution: {
    ProcessingStatus: {
      Name: string,
      At: Date
    }
  };
}

export class DocumentWithCategory {
  type: DocumentTypeCategory;
  item: SupplierDocumentPropertyValues;
}

export class AuditPropertyValue<T> {
  operation: OPERATION;
  oldValue: T;
  newValue: T;
  permission: Permission;
  by: string;
  at: Date;
}

export class TabPermissions {
  address: Permission
  bank: Permission
  contact: Permission
  details: Permission
  document: Permission
  entity: Permission
  status: Permission
  tax: Permission
}

export enum TaxWithHoldingQuestionsEnum {
  USRoyaltiesOrLicensing = '1',
  USRentingTangible = '2',
  USPerformingServices = '3',
  NonUSSellingGoods = '4',
  NonUSPerformingServices = '5'
}

export class FieldConfig {
  required: string;
  name: string;
  allowToggle: boolean;
  defaultOption: string;
  isDocumentAvailableForSupplier: boolean;
  associatedCategoryLinkId: string[];
}

export class FieldConfiguration {
  name: string;
  fields: FieldConfig[]
}

export interface Contract {
  id: number;
  contractID: number;
  contractName: string;
  abbreviation: string;
  contractStatusID: number;
  contractStatusValue: string;
  contractStatusDisplayValue: number;
  businessUnitValue: string;
  businessUnitValueDisplayValue: string;
  externalStorageDocumentID: string;
  externalStorageDocumentExtension: string;
  externalStorageDocumentVersion: number;
  effectiveDate: Date;
  expirationDate: Date;
  signedDate: Date;
  signatureTypeID: number;
  signatureTypeValue: string;
  signatureTypeDisplayValue: string;
  enabled: boolean;
  permission: Permission;
  category: string;
}

export interface DocumentTemplate {
  template: string;
  Description: string;
  templateType: string;
}

export interface SearchResult<T> {
  rows: T[];
  count: number;
}

export interface EHSProperty {
  ID: number;
  Operation: string;
  Response: string;
  TemplateId: number;
  Version: number
}

export interface EHSProperties {
  EhsResponse: EHSProperty[];
}

export class EhsResponse {
  TemplateId: number;
  Version: number;
  Response: string;
}

export class ContextVerbiage {
  onboardingType: Map<string, OnboardingVerbiage[]>
  detailsWithAddress: Verbiage;
  address: Verbiage;
  contact: ContactsContextVerbiage;
  bank: Verbiage;
  tax: Verbiage;
  document: Verbiage;
  diversity: Verbiage;
  ehs: Verbiage;
}
export class ContactsContextVerbiage {
  details: Verbiage;
  primary: Verbiage;
  sales: Verbiage;
  financeLead: Verbiage;
  crp: Verbiage;
  remitTo: Verbiage;
  materialPlanning: Verbiage;
  ehs: Verbiage;
  review: Verbiage;
}

export class OnboardingVerbiage {
  type: string;
  verbiage: Verbiage;
}

export class Verbiage {
  description: string;
  subDescription: string;
  userManual: any;
}

export class RequestFormSection {
  type: RequestFormSectionType;
  supplierData?: any;
  formSchema: any;
  formData: any;

  constructor(type?: RequestFormSectionType, formSchema?: any, formData?, supplierData?) {
    this.type = type;
    this.supplierData = supplierData;
    this.formData = formData;
    this.formSchema = formSchema;
  }
}

export class InsuranceMaster {
  id: string;
  operation: OPERATION
  coverages: InsuranceCoverage[];
  document: SupplierDocument;
  errorMessage: string;

  constructor() {
    this.coverages = [new InsuranceCoverage];
  }
}

export class InsuranceCoverage {
  coverageType: string;
  expiryDate: Date;
  comment?: string;
}

export enum RequestFormSectionType {
  DETAIL = 'detail',
  CONTACT = 'contact',
  ENTITY = 'entity',
  ADDRESS = 'address',
  DOCUMENT = 'document',
  ADDITIONALDETAIL = 'additionalDetail',
  CONFIRMATION = 'confirmation',
  INSURANCE = 'insurance',
}